import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { startGetBrandInformation } from "../actions/adsCampaigns";
import { getIsBlockedActions, getRoleCapability } from "../actions/getters";
import { ConfirmAlert } from "../components/Alerts/Alerts";
import {
  ALERT_FILL_BRAND_SETUP_DESCRIPTION_FOR_CAMPAIGN,
  ALERT_FILL_BRAND_SETUP_TITLE,
  BUTTON_COMPLETE_INFORMATION,
} from "../i18n/keysTranslations";
import { useTranslationApp } from "../lib/i18next";
import { useDispatchApp } from "../lib/redux";
import { useCreditActionCosts } from "./useCreditActionCosts";

/**
 * Shared launcher for "create campaign" (credits + brand context + navigate).
 * Used by Dashboard, AdsCampaigns, and AdsCampaignsIntro.
 */
export const useStartCreateCampaign = ({ fetchBrandOnMount = false } = {}) => {
  const [brandInformation, setBrandInformation] = useState({});

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const navigate = useNavigate();
  const { ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION } = useCreditActionCosts();
  const canCreateCampaign = dispatch(getRoleCapability("canCreateCampaign"));
  const hasBrandContext = Boolean(
    brandInformation.businessName && brandInformation.businessDescription
  );

  const getBrandInformation = useCallback(async () => {
    const response = await dispatch(startGetBrandInformation());
    if (response.ok) {
      setBrandInformation(response.data);
      return response.data;
    }
    return {};
  }, [dispatch]);

  const startCreateCampaign = useCallback(
    async (options) => {
      // Ignore React synthetic events when used directly as onClick.
      const onBeforeNavigate =
        typeof options?.onBeforeNavigate === "function"
          ? options.onBeforeNavigate
          : undefined;

      const isBlocked = await dispatch(
        getIsBlockedActions({
          action: ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION,
          showUsageConfirmationAlert: false,
        })
      );
      if (isBlocked) return;

      if (!hasBrandContext) {
        const response = await ConfirmAlert({
          title: t(ALERT_FILL_BRAND_SETUP_TITLE),
          text: t(ALERT_FILL_BRAND_SETUP_DESCRIPTION_FOR_CAMPAIGN),
          showCancelButton: false,
          confirmButtonText: t(BUTTON_COMPLETE_INFORMATION),
        });
        if (response.isConfirmed) {
          navigate("/settings");
          return;
        }

        if (response.isDismissed && response.dismiss === "backdrop") {
          return;
        }
      }

      onBeforeNavigate?.();
      navigate("/ads-campaign");
    },
    [
      ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION,
      dispatch,
      hasBrandContext,
      navigate,
      t,
    ]
  );

  useEffect(() => {
    if (!fetchBrandOnMount) return;
    getBrandInformation();
  }, [fetchBrandOnMount, getBrandInformation]);

  return {
    startCreateCampaign,
    canCreateCampaign,
    brandInformation,
    getBrandInformation,
  };
};
